"use client"

import {
  useQualityChangesQuery,
  useQualitySuggestionsQuery,
  useSelfLearningSummaryQuery,
  useUnresolvedTopicsQuery,
} from "@/hooks/queries/conversation-quality"

import { Changes } from "../sections/change-sections"
import { NeedsDecision } from "../sections/decision-sections"
import { SelfLearningStrip } from "../sections/self-learning-strip"
import { UnresolvedTopics } from "../sections/topic-sections"
import { ErrorAlert, useLanding, type QualityTabProps } from "./tab-parts"

/**
 * One column: how the agents learn, the questions they didn't resolve, what waits for a decision, every change with
 * its state. No page notice: only the questions follow the period, and their card says itself when it has none.
 */
export function LearningTab({ input, viewerTimezone, ready, quality }: QualityTabProps) {
  const on = quality === "on"
  const { data: topics, error: topicsError } = useUnresolvedTopicsQuery(input, ready && on)
  // No timezone involved, so these need not wait for the profile.
  const listInput = { agentId: input.agentId }
  const { data: suggestions, error: suggestionsError } = useQualitySuggestionsQuery(listInput, on)
  const { data: summary, error: summaryError } = useSelfLearningSummaryQuery(listInput, on)
  const changes = useQualityChangesQuery(listInput, on)
  const [section, landedOnSection] = useLanding("section")
  const [suggestion, landedOnSuggestion] = useLanding("suggestion")

  if (quality === "off") return null

  return (
    <div className="space-y-3">
      {summaryError ? (
        <ErrorAlert error={summaryError} />
      ) : (
        <SelfLearningStrip summary={summary} digest={suggestions?.digest} />
      )}
      {topicsError ? (
        <ErrorAlert error={topicsError} />
      ) : (
        <UnresolvedTopics
          data={topics}
          suggestions={suggestions?.suggestions}
          landing={section === "questions"}
          onLanded={landedOnSection}
        />
      )}
      {suggestionsError ? (
        <ErrorAlert error={suggestionsError} />
      ) : (
        <NeedsDecision
          data={suggestions}
          agentId={input.agentId}
          timezone={viewerTimezone}
          landing={suggestion}
          onLanded={landedOnSuggestion}
        />
      )}
      {changes.error ? (
        <ErrorAlert error={changes.error} />
      ) : (
        <Changes
          changes={changes.data?.pages.flatMap((page) => page.changes)}
          total={changes.data?.pages.at(-1)?.total ?? 0}
          showAgent={!input.agentId}
          timezone={viewerTimezone}
          hasOlder={changes.hasNextPage}
          loadingOlder={changes.isFetchingNextPage}
          onShowOlder={() => void changes.fetchNextPage()}
          landing={section === "changes"}
          onLanded={landedOnSection}
        />
      )}
    </div>
  )
}
